/**
 * Session validation utilities for the 30s duration rule.
 *
 * Sessions created after DURATION_RULE_CUTOFF_DATE must have a duration >= 30s
 * to be included in statistics. Old sessions are always included, but their
 * duration may be hidden if it's very short.
 */

import { DURATION_RULE_CUTOFF_DATE, MIN_SESSION_DURATION } from "../constants";

/**
 * Check if a session is an "old" session (created before the cutoff date).
 * Old sessions are always included in stats regardless of duration.
 */
export const isOldSession = (createdAt: string | number): boolean => {
  const date = typeof createdAt === "number" ? new Date(createdAt * 1000) : new Date(createdAt);
  return date < DURATION_RULE_CUTOFF_DATE;
};

/**
 * Check if the duration should be hidden for display purposes.
 * For old sessions with very short durations (< 30s), we hide the duration
 * to avoid confusion, but the session itself remains visible in stats.
 */
export const shouldHideDuration = (
  createdAt: string | number,
  duration: number | null,
): boolean => {
  // Only hide duration for old sessions with short duration
  return isOldSession(createdAt) && duration != null && duration < MIN_SESSION_DURATION;
};

/**
 * Session interface for validation (minimal fields needed)
 */
interface SessionForValidation {
  createdAt?: string;
  created_at_ts?: number;
  total_session_duration?: number | null;
}

/**
 * Check if a session is valid for statistics display.
 * - Old sessions: always valid (regardless of duration)
 * - New sessions: must have duration >= 30s (null = anomaly, excluded)
 *
 * Note: The backend already filters new sessions < 30s, so this is mainly
 * for client-side validation in case of direct data manipulation.
 */
export const isValidSessionForStats = (session: SessionForValidation): boolean => {
  const createdAt = session.createdAt || session.created_at_ts;
  if (!createdAt) {
    return false;
  }

  // Old sessions are always valid
  if (isOldSession(createdAt)) {
    return true;
  }

  // New sessions: duration >= 30s required (null = anomaly, excluded)
  const duration = session.total_session_duration;
  return duration != null && duration >= MIN_SESSION_DURATION;
};

/**
 * Check if a duration value should be included in average calculations.
 * For old sessions, we exclude very short durations from averages
 * to avoid skewing the data.
 */
export const shouldIncludeDurationInAverage = (
  createdAt: string | number,
  duration: number | null,
): boolean => {
  if (duration == null) {
    return false;
  }

  // For old sessions, exclude very short durations from averages
  if (isOldSession(createdAt)) {
    return duration >= MIN_SESSION_DURATION;
  }

  // For new sessions, the backend already filters, so include all
  return true;
};
